import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Tooltip, Link, ListItemText } from '@mui/material';
// locales
import { useLocales } from '../../../hooks';
// auth
import { RoleBasedGuard } from '../../../guards';
//
import Iconify from '../../Iconify';
import { StyledItem, StyledIcon } from './styles';

// ----------------------------------------------------------------------

const NavItem = forwardRef(({ item, depth, open, active, isExternalLink, ...other }, ref) => {
    const { translate } = useLocales();

    const { title, path, icon, children, disabled, caption, roles } = item;

    const subItem = depth !== 1;

    const renderContent = (
        <StyledItem ref={ref} open={open} depth={depth} active={active} disabled={disabled} {...other}>
            {icon && <StyledIcon>{icon}</StyledIcon>}

            {subItem && (
                <ListItemText
                    primary={`${translate(title)}`}
                    primaryTypographyProps={{
                        noWrap: true,
                        sx: {
                            fontSize: 14,
                            width: 'auto',
                            textAlign: 'left',
                            ...(active && {
                                fontWeight: 'fontWeightMedium',
                            }),
                        },
                    }}
                />
            )}

            {caption && (
                <Tooltip title={`${translate(caption)}`} arrow placement="right">
                    <Iconify
                        icon="eva:info-outline"
                        width={16}
                        sx={{
                            top: 11,
                            left: 6,
                            position: 'absolute',
                        }}
                    />
                </Tooltip>
            )}

            {subItem && !!children && (
                <Iconify
                    width={16}
                    icon="eva:chevron-right-fill"
                    sx={{ ml: 'auto', flexShrink: 0 }}
                />
            )}
        </StyledItem>
    );

    const renderItem = () => {
        const linkEl = isExternalLink
            ? <Link href={path} target="_blank" rel="noopener" underline="none">{renderContent}</Link>
            : <Link component={RouterLink} to={path} underline="none">{renderContent}</Link>;

        // Top-level leaf items get a tooltip showing the title on hover
        if (!subItem && !children) {
            return (
                <Tooltip title={translate(title)} placement="right" arrow>
                    {linkEl}
                </Tooltip>
            );
        }
        return linkEl;
    };

    return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
});

NavItem.propTypes = {
    open: PropTypes.bool,
    active: PropTypes.bool,
    item: PropTypes.object,
    depth: PropTypes.number,
    isExternalLink: PropTypes.bool,
};

export default NavItem;
